import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext.jsx';
import {
  STATUS_LABELS, TICKET_STATUSES, isUserOnTicket, fmtRelative
} from '../lib/tickets.js';
import { TicketStatusBadge, TicketPriorityBadge, StatusBar, AssigneeCell } from '../components/TicketBits.jsx';
import NewTicketModal from '../components/NewTicketModal.jsx';

export default function Tickets() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | <status>
  const [assigneeFilter, setAssigneeFilter] = useState('all'); // all | me | unassigned
  const [modalOpen, setModalOpen] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await axios.get('/api/tickets');
      setTickets(res.data.data || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const now = new Date();
  const counts = useMemo(() => {
    const c = { all: tickets.length };
    for (const s of TICKET_STATUSES) c[s] = 0;
    for (const t of tickets) c[t.status] = (c[t.status] || 0) + 1;
    return c;
  }, [tickets]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tickets.filter((t) => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (assigneeFilter === 'me' && !isUserOnTicket(t, user?.id)) return false;
      if (assigneeFilter === 'unassigned' && (t.primaryAssigneeId != null || t.collaborators.length > 0)) return false;
      if (q) {
        const hay = [t.title, t.description, t.siteName].map((v) => String(v || '').toLowerCase()).join(' | ');
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tickets, query, statusFilter, assigneeFilter, user]);

  const myOpen = tickets.filter((t) => t.status !== 'closed' && isUserOnTicket(t, user?.id)).length;
  const allOpen = tickets.filter((t) => t.status !== 'closed').length;
  const waiting = tickets.filter((t) => t.status === 'waiting').length;

  return (
    <section className="page">
      <div className="page-header">
        <div>
          <h2>Tickets</h2>
          <p className="page-sub">Track support issues — who's working on what, and what's waiting.</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setModalOpen(true)}>+ New Ticket</button>
      </div>

      <div className="kpi-strip">
        <div className={`kpi-tile ${myOpen > 0 ? 'kpi-warn' : ''}`}>
          <div className="kpi-value">{myOpen}</div>
          <div className="kpi-label">My open tickets</div>
        </div>
        <div className="kpi-tile">
          <div className="kpi-value">{allOpen}</div>
          <div className="kpi-label">All open tickets</div>
        </div>
        <div className="kpi-tile">
          <div className="kpi-value">{waiting}</div>
          <div className="kpi-label">Waiting on customer</div>
        </div>
        <div className="kpi-tile">
          <div className="kpi-value">{counts.closed || 0}</div>
          <div className="kpi-label">Closed</div>
        </div>
      </div>

      <div className="toolbar">
        <input
          type="search" className="search-input"
          placeholder="Search title, description, or site…"
          value={query} onChange={(e) => setQuery(e.target.value)}
        />
        <div className="chip-group">
          {['all', ...TICKET_STATUSES].map((s) => (
            <button
              key={s} type="button"
              className={`chip ${statusFilter === s ? 'chip-active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s === 'all' ? 'All' : STATUS_LABELS[s]}
              <span className="chip-count">{counts[s] || 0}</span>
            </button>
          ))}
        </div>
        <select className="layout-dropdown" value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)}>
          <option value="all">All assignees</option>
          <option value="me">Me</option>
          <option value="unassigned">Unassigned</option>
        </select>
        <span className="result-count">
          {loading ? 'Loading…' : `${filtered.length} ${filtered.length === 1 ? 'ticket' : 'tickets'}`}
        </span>
      </div>

      {error && <p className="error">Error: {error}</p>}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th className="th-statusbar" />
              <th>Site</th>
              <th>Title</th>
              <th>Assigned to</th>
              <th>Priority</th>
              <th>Last updated</th>
              <th className="th-num">Notes</th>
              <th aria-label="Delete" />
            </tr>
          </thead>
          <tbody>
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="empty-row">No tickets match the current filters.</td></tr>
            )}
            {filtered.map((t) => (
              <tr key={t.id} className="row-clickable" onClick={() => navigate(`/tickets/${t.id}`)}>
                <td className="td-statusbar"><StatusBar status={t.status} /></td>
                <td>
                  {t.siteName
                    ? <div className="cell-primary">{t.siteName}</div>
                    : <span className="cell-muted" style={{ fontStyle: 'italic' }}>Internal</span>}
                </td>
                <td>
                  <span className="ticket-title-cell">
                    <TicketStatusBadge value={t.status} />
                    {t.source === 'email' && (
                      <span className="badge badge-purple" title={`From: ${t.inboundFromEmail}`}>📧 EMAIL</span>
                    )}
                    {t.source === 'email' && !t.contactId && (
                      <span className="badge badge-amber" title="Sender not matched to any contact">⚠ UNKNOWN</span>
                    )}
                    <span className={t.status === 'closed' ? 'ticket-title-closed' : 'ticket-title'}>{t.title}</span>
                  </span>
                </td>
                <td><AssigneeCell ticket={t} /></td>
                <td>{t.priority === 'normal' ? <span className="cell-mono cell-muted">—</span> : <TicketPriorityBadge value={t.priority} />}</td>
                <td className="cell-mono">{fmtRelative(t.updatedAt, now)}</td>
                <td className="th-num cell-mono">{t.notesCount}</td>
                <td
                  className="cell-actions"
                  onClick={(e) => e.stopPropagation()}
                  style={{ width: '40px', textAlign: 'right' }}
                >
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    aria-label={`Delete ticket ${t.id}`}
                    title={`Delete ticket #${t.id} ("${t.title || 'no title'}")`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!window.confirm(`Delete ticket #${t.id}?\n\n"${t.title || 'no title'}"\n\nThis cannot be undone.`)) return;
                      try {
                        await axios.delete(`/api/tickets/${t.id}`);
                        setTickets((prev) => prev.filter((x) => x.id !== t.id));
                      } catch (err) {
                        window.alert(`Delete failed: ${err.response?.data?.error || err.message}`);
                      }
                    }}
                    style={{ color: 'var(--color-bad)', padding: '2px 8px' }}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <NewTicketModal
        open={modalOpen}
        currentUserId={user?.id}
        onCancel={() => setModalOpen(false)}
        onCreated={(ticket) => { setModalOpen(false); setTickets((p) => [ticket, ...p]); navigate(`/tickets/${ticket.id}`); }}
      />
    </section>
  );
}
